import gestionApiService from '@/core/services/gestionApiService.ts'
import { responseSchema } from '@/core/types/Response.ts'
import { z } from 'zod'
import { validateSchemaAsync } from '@/core/utils/safeSchemaValidations.ts'
import {
  numericSelectOptionBackSchema,
  selectOptionBackSchema
} from '@/core/types/SelectOption.ts'
import walletApiService from '@/core/services/walletApiService.ts'
import { PaymentMethodList } from '@/modules/settings/paymentMethod/types/types.ts'
import { Bank, bankSchema } from '@/core/types/Bank.ts'
import {
  rentabilitySchema,
  RentabilityType
} from '@/modules/settings/tasa/types/types.ts'
import { insuranceRentabilitySchema } from '@/modules/insurers/insuranceRentability/types/InsuranceRentability.ts'

const selectOptionsSchema = z.array(selectOptionBackSchema)
export const selectOptionsResponseSchema = responseSchema(selectOptionsSchema)
export type SelectResponse = z.infer<typeof selectOptionsResponseSchema>

const locationSelectOptionsSchema = z.array(numericSelectOptionBackSchema)
export const locationSelectOptionsResponseSchema = responseSchema(
  locationSelectOptionsSchema
)
export type LocationSelectResponse = z.infer<
  typeof locationSelectOptionsResponseSchema
>

const getActiveSystemRoles = () =>
  validateSchemaAsync(
    selectOptionsResponseSchema,
    gestionApiService.httpGET<SelectResponse>('roles/catalog')
  )

const getCompanyBranches = () =>
  validateSchemaAsync(
    selectOptionsResponseSchema,
    gestionApiService.httpGET<SelectResponse>('branch-offices/catalog')
  )

const getEmployees = () =>
  validateSchemaAsync(
    selectOptionsResponseSchema,
    gestionApiService.httpGET<SelectResponse>('employee/catalog')
  )

const getArticleTypes = () =>
  validateSchemaAsync(
    selectOptionsResponseSchema,
    gestionApiService.httpGET<SelectResponse>('product-types/active')
  )

const getArticleCategories = () =>
  validateSchemaAsync(
    selectOptionsResponseSchema,
    gestionApiService.httpGET<SelectResponse>('categories/active')
  )

const getArticleCategoriesCatalog = () =>
  validateSchemaAsync(
    selectOptionsResponseSchema,
    gestionApiService.httpGET<SelectResponse>('categories/catalog')
  )

const getActiveBrands = (supplierId?: string) =>
  validateSchemaAsync(
    selectOptionsResponseSchema,
    gestionApiService.httpGET<SelectResponse>(
      `brands/catalog${supplierId ? `?supplierId=${supplierId}` : ''}`
    )
  )

const brandOptionResponseSchema = responseSchema(
  z.array(z.object({ name: z.string(), id: z.string() }))
)
type BrandOptionResponse = z.infer<typeof brandOptionResponseSchema>
const getFullBrands = () =>
  gestionApiService.httpGET<BrandOptionResponse>(`brands/active`)

const getSuppliers = () =>
  validateSchemaAsync(
    selectOptionsResponseSchema,
    gestionApiService.httpGET<SelectResponse>('suppliers/catalog')
  )

const modelOptionsSchema = z.array(
  selectOptionBackSchema.merge(z.object({ maxCost: z.string() }))
)
const modelOptionResponseSchema = responseSchema(modelOptionsSchema)
type ModelOptionResponse = z.infer<typeof modelOptionResponseSchema>
const getActiveModels = (brandId?: string) =>
  validateSchemaAsync(
    modelOptionResponseSchema,
    gestionApiService.httpGET<ModelOptionResponse>(
      `models/catalog${brandId ? `?brand=${brandId}` : ''}`
    )
  )

const getAutomotiveBrands = () =>
  gestionApiService.httpGET<{ id: string; name: string }[]>(
    `brands/automotive_product`
  )

const getAutomotiveModels = (brandId?: string) =>
  gestionApiService.httpGET<{ id: string; name: string }[]>(
    `models/automotive_product${brandId ? `?brand=${brandId}` : ''}`
  )

const getPaymentTypes = () =>
  walletApiService.httpGET<PaymentMethodList>(`/payment-type`)

const getStatesCatalog = () =>
  validateSchemaAsync(
    locationSelectOptionsResponseSchema,
    gestionApiService.httpGET<LocationSelectResponse>('states/catalog')
  )

const getSectorsCatalog = (filter?: string) =>
  validateSchemaAsync(
    locationSelectOptionsResponseSchema,
    gestionApiService.httpGET<LocationSelectResponse>(
      `sectors/catalog${filter ? filter : ''}`
    )
  )

const getCitiesCatalog = (filter?: string) =>
  validateSchemaAsync(
    locationSelectOptionsResponseSchema,
    gestionApiService.httpGET<LocationSelectResponse>(
      `cities/catalog${filter ? filter : ''}`
    )
  )

const rentabilityOptionsSchema = z.array(rentabilitySchema)
const rentabilityOptionResponseSchema = responseSchema(rentabilityOptionsSchema)
type RentabilityOptionResponse = z.infer<typeof rentabilityOptionResponseSchema>
const getRentabilities = () =>
  validateSchemaAsync(
    rentabilityOptionResponseSchema,
    gestionApiService.httpGET<RentabilityOptionResponse>('rentabilities/active')
  )

const getMiscellaneousRentabilities = () =>
  validateSchemaAsync(
    rentabilityOptionResponseSchema,
    gestionApiService.httpGET<RentabilityOptionResponse>(
      'rentabilities/active?type=MISCELLANEOUS'
    )
  )

const getRentabilitiesByType = (type: RentabilityType) =>
  validateSchemaAsync(
    rentabilityOptionResponseSchema,
    gestionApiService.httpGET<RentabilityOptionResponse>(
      `rentabilities/active?type=${type}`
    )
  )

const insuranceRentabilityOptionResponseSchema = responseSchema(
  z.array(insuranceRentabilitySchema)
)
export type InsuranceRentabilityOptionResponseSchema = z.infer<
  typeof insuranceRentabilityOptionResponseSchema
>
const getInsuranceRentabilities = () =>
  validateSchemaAsync(
    insuranceRentabilityOptionResponseSchema,
    gestionApiService.httpGET<InsuranceRentabilityOptionResponseSchema>(
      'insurance_rentability/active'
    )
  )

const getRentabilitiesByCategory = (categoryId: string) =>
  validateSchemaAsync(
    rentabilityOptionResponseSchema,
    gestionApiService.httpGET<RentabilityOptionResponse>(
      `rentabilities/by-category/${categoryId}`
    )
  )

const getTradeMarksCatalog = (allyId?: string) =>
  validateSchemaAsync(
    rentabilityOptionResponseSchema,
    gestionApiService.httpGET<SelectResponse>(
      `trademarks/catalog${allyId ? '?commercialAllyId=' + allyId : ''}`
    )
  )

const getClientTypeCatalog = () =>
  validateSchemaAsync(
    selectOptionsResponseSchema,
    gestionApiService.httpGET<SelectResponse>('client-types/catalog')
  )

const getAlliesCatalog = () =>
  validateSchemaAsync(
    selectOptionsResponseSchema,
    gestionApiService.httpGET<SelectResponse>('commercial-allies/catalog')
  )

const getCommercialAlliesTypeCatalog = () =>
  validateSchemaAsync(
    selectOptionsResponseSchema,
    gestionApiService.httpGET<SelectResponse>('commercial-allies-type/catalog')
  )

const getVideoCategories = () =>
  validateSchemaAsync(
    selectOptionsResponseSchema,
    gestionApiService.httpGET<SelectResponse>('video/categories/catalog')
  )

const getCompanyBranchesCatalog = (allieIds?: string[]) => {
  const commercialAllyId = allieIds
    ? `?commercialAllyId=${allieIds.join(',')}`
    : ''
  return validateSchemaAsync(
    selectOptionsResponseSchema,
    gestionApiService.httpGET<SelectResponse>(
      `branch-offices/catalog${commercialAllyId}`
    )
  )
}

const bankListResponseSchema = z.array(bankSchema)
const bankListResponseSchemaResponse = responseSchema(bankListResponseSchema)
const getReceptorBanks = () =>
  validateSchemaAsync(
    bankListResponseSchemaResponse,
    walletApiService.httpGET<Bank[]>('currency-payment-bank/receiver-banks')
  )

const getClientLevelsCatalog = () =>
  validateSchemaAsync(
    selectOptionsResponseSchema,
    gestionApiService.httpGET<SelectResponse>('loyalty/level/catalog')
  )

const getInsuranceProviders = () =>
  validateSchemaAsync(
    selectOptionsResponseSchema,
    gestionApiService.httpGET<SelectResponse>(`insurance_providers/catalog`)
  )

const getBanksCatalog = () =>
  validateSchemaAsync(
    selectOptionsResponseSchema,
    gestionApiService.httpGET<SelectResponse>('banks/catalog')
  )

const branchOfficeDisableReasonsResponseSchema = responseSchema(
  z.array(
    z.object({
      id: z.union([z.string(), z.number()]),
      name: z.string()
    })
  )
)
type BranchOfficeDisableReasonsResponse = z.infer<
  typeof branchOfficeDisableReasonsResponseSchema
>

/**
 * `selectable: true` returns only the reasons that can be chosen when
 * disabling/editing. Without it, "Sin motivo registrado" is also included
 * (used by the report filter).
 */
const getBranchOfficeDisableReasonsCatalog = (selectable = false) =>
  validateSchemaAsync(
    branchOfficeDisableReasonsResponseSchema,
    gestionApiService.httpGET<BranchOfficeDisableReasonsResponse>(
      `branch-office-disable-reasons/catalog${selectable ? '?selectable=true' : ''}`
    )
  )

const selectServices = {
  getActiveSystemRoles,
  getCompanyBranches,
  getEmployees,
  getArticleTypes,
  getArticleCategories,
  getArticleCategoriesCatalog,
  getActiveBrands,
  getFullBrands,
  getSuppliers,
  getActiveModels,
  getAutomotiveBrands,
  getAutomotiveModels,
  getPaymentTypes,
  getStatesCatalog,
  getRentabilities,
  getMiscellaneousRentabilities,
  getRentabilitiesByType,
  getInsuranceRentabilities,
  getRentabilitiesByCategory,
  getSectorsCatalog,
  getCitiesCatalog,
  getClientTypeCatalog,
  getTradeMarksCatalog,
  getAlliesCatalog,
  getCommercialAlliesTypeCatalog,
  getVideoCategories,
  getCompanyBranchesCatalog,
  getReceptorBanks,
  getClientLevelsCatalog,
  getInsuranceProviders,
  getBanksCatalog,
  getBranchOfficeDisableReasonsCatalog
}

export default selectServices
